/**
 * Mock API adapter for prevalidation testing.
 *
 * Intercepts all axios requests and returns test fixtures.
 * Simulates realistic latency and validates request shapes.
 *
 * Toggle via: VITE_USE_MOCKS=true in .env or .env.local
 *
 * Usage:
 *   1. Set VITE_USE_MOCKS=true
 *   2. Run `npm run dev`
 *   3. Login with any username/password (use "testuser" / "test123" for success)
 *   4. All API calls return fixture data with simulated latency
 */

import type { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import * as fixtures from './fixtures';

const MOCK_LATENCY_MS = 300;

interface MockRoute {
  match: (url: string, method: string) => boolean;
  handler: (config: InternalAxiosRequestConfig) => unknown;
  description: string;
}

// Safely parse request body — config.data may already be an object (not yet serialized)
function parseBody(data: unknown): Record<string, unknown> {
  if (!data) return {};
  if (typeof data === 'string') return JSON.parse(data) as Record<string, unknown>;
  if (typeof data === 'object') return data as Record<string, unknown>;
  return {};
}

// Validation log
const validationLog: { endpoint: string; status: string; details: string }[] = [];

function log(endpoint: string, status: 'OK' | 'WARN' | 'ERROR', details: string) {
  validationLog.push({ endpoint, status, details });
  const color = status === 'OK' ? '\x1b[32m' : status === 'WARN' ? '\x1b[33m' : '\x1b[31m';
  console.log(`${color}[MOCK ${status}]\x1b[0m ${endpoint}: ${details}`);
}

function validateRequired(data: unknown, fields: string[], endpoint: string): boolean {
  if (!data || typeof data !== 'object') {
    log(endpoint, 'ERROR', `Request body is not an object: ${JSON.stringify(data)}`);
    return false;
  }
  const missing = fields.filter((f) => !(f in (data as Record<string, unknown>)));
  if (missing.length > 0) {
    log(endpoint, 'WARN', `Missing fields in request: ${missing.join(', ')}`);
    return false;
  }
  log(endpoint, 'OK', `Request validated (${fields.length} fields checked)`);
  return true;
}

// Define all mock routes
const routes: MockRoute[] = [
  {
    match: (url) => url.includes('/Auth/Authenticate'),
    description: 'UserPortal Auth/Authenticate (POST)',
    handler: (config) => {
      const data = parseBody(config.data);
      const user = data.username as string;
      const pass = data.password as string;
      log('Auth/Authenticate', 'OK', `Login attempt: user="${user}"`);

      if (user === 'baduser' || pass === 'badpass') {
        return { status: 401, error: 'Invalid username or password' };
      }

      // Return a fake JWT token (base64-encoded JSON header.payload.signature)
      const header = btoa(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));
      const payload = btoa(JSON.stringify({
        iss: 'nautic-alert-user-portal',
        sub: 'acct-uuid-1234-5678-abcd',
        username: user || 'testuser',
        cellNumber: '+15551234567',
        termsAndConditionsAccepted: true,
        clientId: 'mock-client-id',
        auth_time: Math.floor(Date.now() / 1000),
        exp: Math.floor(Date.now() / 1000) + 3600,
      }));
      const fakeToken = `${header}.${payload}.mock-signature`;

      return {
        accessToken: fakeToken,
        accountUUID: 'acct-uuid-1234-5678-abcd',
        userID: user || 'testuser',
        termsAndConditionsAccepted: true,
      };
    },
  },
  {
    match: (url) => url.includes('/Auth/RefreshToken'),
    description: 'UserPortal Auth/RefreshToken (POST)',
    handler: () => {
      const header = btoa(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));
      const payload = btoa(JSON.stringify({
        iss: 'nautic-alert-user-portal',
        sub: 'acct-uuid-1234-5678-abcd',
        username: 'testuser',
        exp: Math.floor(Date.now() / 1000) + 3600,
      }));
      log('Auth/RefreshToken', 'OK', 'Token refreshed');
      return { accessToken: `${header}.${payload}.mock-signature-refreshed` };
    },
  },
  {
    match: (url) => url.includes('/Auth/AccountOperations') || url.includes('accountOperationsV2'),
    description: 'UserPortal Auth/AccountOperations (POST)',
    handler: (config) => {
      const data = parseBody(config.data);
      validateRequired(data, ['operationType'], 'accountOperationsV2');
      const op = data.operationType as string;
      log('accountOperationsV2', 'OK', `Operation: ${op}`);

      // Simulate verification code validation for POST operations
      if (op === 'ACCOUNT_OPERATION_POST_CREATE_VERIFY' || op === 'ACCOUNT_OPERATION_POST_CHANGE_PASSWORD_VERIFY') {
        const code = data.verificationCode as string;
        if (code === '99999') {
          log('accountOperationsV2', 'WARN', 'Invalid verification code (test: use any code except 99999)');
          return fixtures.wrapResponse(null, {
            isAuthenticated: false,
          });
        }
      }

      return fixtures.wrapResponse(null);
    },
  },
  {
    match: (url) => url.includes('/Devices/AccountDevices') || url.includes('accountDevicesV2'),
    description: 'accountDevicesV2 (POST)',
    handler: (config) => {
      const data = parseBody(config.data);
      validateRequired(data, ['accountUUID'], 'accountDevicesV2');
      log('accountDevicesV2', 'OK', `Returning ${fixtures.accountDevicesResponse.deviceList.length} devices`);
      return {
        ...fixtures.authSuccessResponse,
        containerObj: fixtures.accountDevicesResponse,
      };
    },
  },
  {
    match: (url) => url.includes('/Devices/DeviceInfo') || url.includes('deviceInfoV2'),
    description: 'deviceInfoV2 (POST)',
    handler: (config) => {
      const data = parseBody(config.data);
      validateRequired(data, ['deviceUUID'], 'deviceInfoV2');

      const sensorFlags = [];
      if (data.includeSensorData) sensorFlags.push('sensors');
      if (data.includeEventList) sensorFlags.push('events');
      if (data.includeMTList) sensorFlags.push('MT');
      log('deviceInfoV2', 'OK', `Device: ${data.deviceUUID}, includes: [${sensorFlags.join(', ')}]`);

      return {
        ...fixtures.authSuccessResponse,
        containerObj: fixtures.deviceInfoResponse,
      };
    },
  },
  {
    match: (url) => url.includes('/Devices/DeviceProperties') || url.includes('devicePropertiesV2'),
    description: 'devicePropertiesV2 (POST)',
    handler: (config) => {
      const data = parseBody(config.data);
      validateRequired(data, ['deviceUUID', 'containedObj'], 'devicePropertiesV2');
      log('devicePropertiesV2', 'OK', `Setting properties for ${data.deviceUUID}`);
      return fixtures.authSuccessResponse;
    },
  },
  {
    match: (url) => url.includes('/Devices/LocationTracks') || url.includes('locationTrackRequestV2'),
    description: 'locationTrackRequestV2 (POST)',
    handler: (config) => {
      const data = parseBody(config.data);
      validateRequired(data, ['deviceUUID'], 'locationTrackRequestV2');
      const totalPoints = fixtures.tracksResponse.tracksList.reduce((s, t) => s + t.length, 0);
      log('locationTrackRequestV2', 'OK',
        `${fixtures.tracksResponse.tracksList.length} tracks, ${totalPoints} points, days=${data.maxNumDays || 10}`);
      return {
        ...fixtures.authSuccessResponse,
        containerObj: fixtures.tracksResponse,
      };
    },
  },
  {
    match: (url) => url.includes('/Geofence/List') || url.includes('globalGeofenceListRequestV2'),
    description: 'globalGeofenceListRequestV2 (POST)',
    handler: (config) => {
      const data = parseBody(config.data);
      validateRequired(data, ['deviceUUID'], 'globalGeofenceListRequestV2');
      log('globalGeofenceListRequestV2', 'OK', `${fixtures.geofenceListResponse.length} geofences`);
      return {
        ...fixtures.authSuccessResponse,
        containerObj: fixtures.geofenceListResponse,
      };
    },
  },
  {
    match: (url) => url.includes('/Geofence/Add') || url.includes('globalGeofenceAddEntryV2'),
    description: 'globalGeofenceAddEntryV2 (POST)',
    handler: (config) => {
      const data = parseBody(config.data);
      validateRequired(data, ['deviceUUID', 'geoName', 'lat', 'lon', 'radius'], 'globalGeofenceAddEntryV2');
      if (Number(data.radius) < 50) {
        log('globalGeofenceAddEntryV2', 'WARN', `Radius ${data.radius}m below minimum 50m`);
      }
      log('globalGeofenceAddEntryV2', 'OK', `Adding "${data.geoName}" at ${data.lat},${data.lon} r=${data.radius}m`);
      return fixtures.authSuccessResponse;
    },
  },
  {
    match: (url) => url.includes('/Geofence/Delete') || url.includes('globalGeofenceDeleteEntryV2'),
    description: 'globalGeofenceDeleteEntryV2 (POST)',
    handler: (config) => {
      const data = parseBody(config.data);
      validateRequired(data, ['deviceUUID', 'geofenceId'], 'globalGeofenceDeleteEntryV2');
      log('globalGeofenceDeleteEntryV2', 'OK', `Deleting geofence ${data.geofenceId}`);
      return fixtures.authSuccessResponse;
    },
  },
  {
    match: (url) => url.includes('/Devices/EventLog') || url.includes('eventLogV2'),
    description: 'eventLogV2 (POST)',
    handler: (config) => {
      const data = parseBody(config.data);
      validateRequired(data, ['deviceUUID'], 'eventLogV2');
      log('eventLogV2', 'OK', `${fixtures.eventLogResponse.eventList.length} events, days=${data.maxNumDays || 30}`);
      return {
        ...fixtures.authSuccessResponse,
        containerObj: fixtures.eventLogResponse,
      };
    },
  },
  {
    match: (url) => url.includes('/Devices/OnDemandRequest') || url.includes('onDemandRequestV2'),
    description: 'onDemandRequestV2 (POST)',
    handler: (config) => {
      const data = parseBody(config.data);
      validateRequired(data, ['deviceUUID', 'mtMessageType'], 'onDemandRequestV2');
      log('onDemandRequestV2', 'OK', `Command: ${data.mtMessageType}, MTMSN: ${fixtures.onDemandSuccessResponse.mtmsn}`);
      return {
        ...fixtures.authSuccessResponse,
        containerObj: fixtures.onDemandSuccessResponse,
      };
    },
  },
  {
    match: (url) => url.includes('/Devices/ContactList') || url.includes('contactListV2'),
    description: 'contactListV2 (POST)',
    handler: (config) => {
      const data = parseBody(config.data);
      validateRequired(data, ['deviceUUID', 'entries'], 'contactListV2');
      log('contactListV2', 'OK', `${data.addEntries ? 'Adding' : 'Removing'} ${Array.isArray(data.entries) ? data.entries.length : 0} contacts`);
      return fixtures.authSuccessResponse;
    },
  },
  {
    match: (url) => url.includes('/Devices/PumpActivityLog') || url.includes('pumpActivityLogV2'),
    description: 'pumpActivityLogV2 (POST)',
    handler: (config) => {
      const data = parseBody(config.data);
      validateRequired(data, ['deviceUUID', 'nevataAddress'], 'pumpActivityLogV2');
      log('pumpActivityLogV2', 'OK', `${fixtures.pumpActivityLogResponse.entries.length} entries`);
      return {
        ...fixtures.authSuccessResponse,
        containerObj: fixtures.pumpActivityLogResponse,
      };
    },
  },
  {
    match: (url) => url.includes('/Settings/VMS') || url.includes('vmsSettingsV2'),
    description: 'vmsSettingsV2 (POST)',
    handler: (config) => {
      const data = parseBody(config.data);
      validateRequired(data, ['deviceUUID'], 'vmsSettingsV2');
      if (data.settingsObj) {
        log('vmsSettingsV2', 'OK', 'Setting VMS settings');
        return fixtures.authSuccessResponse;
      }
      log('vmsSettingsV2', 'OK', 'Getting VMS settings');
      return {
        ...fixtures.authSuccessResponse,
        containerObj: fixtures.vmsSettingsResponse,
      };
    },
  },
  {
    match: (url) => url.includes('/Settings/VoiceCall') || url.includes('voiceCallSettingsV2'),
    description: 'voiceCallSettingsV2 (POST)',
    handler: (config) => {
      const data = parseBody(config.data);
      validateRequired(data, ['deviceUUID'], 'voiceCallSettingsV2');
      if (data.settingsObj) {
        log('voiceCallSettingsV2', 'OK', 'Setting voice call settings');
        return fixtures.authSuccessResponse;
      }
      log('voiceCallSettingsV2', 'OK', 'Getting voice call settings');
      return {
        ...fixtures.authSuccessResponse,
        containerObj: fixtures.voiceCallSettingsResponse,
      };
    },
  },
  {
    match: (url, method) => url.includes('/Terms') || url.includes('nauticAlertTermsAndConditionsV2') && method === 'get',
    description: 'nauticAlertTermsAndConditionsV2 (GET)',
    handler: () => {
      log('nauticAlertTermsAndConditionsV2', 'OK', 'Returning T&C text');
      return {
        ...fixtures.authSuccessResponse,
        containerObj: fixtures.termsAndConditionsText,
      };
    },
  },
  {
    match: (url, method) => url.includes('/Terms') || url.includes('nauticAlertTermsAndConditionsV2') && method === 'post',
    description: 'nauticAlertTermsAndConditionsV2 (POST)',
    handler: (config) => {
      const data = parseBody(config.data);
      validateRequired(data, ['accountUUID'], 'nauticAlertTermsAndConditionsV2');
      log('nauticAlertTermsAndConditionsV2', 'OK', 'T&C accepted');
      return fixtures.authSuccessResponse;
    },
  },
];

/**
 * Install mock interceptors on an axios instance.
 * Returns a function to remove them.
 */
export function installMockAdapter(client: AxiosInstance): () => void {
  const interceptorId = client.interceptors.request.use(async (config) => {
    const url = config.url || '';
    const method = (config.method || 'get').toLowerCase();

    const route = routes.find((r) => r.match(url, method));

    if (route) {
      // Simulate network latency
      await new Promise((resolve) => setTimeout(resolve, MOCK_LATENCY_MS));

      const responseData = route.handler(config);

      // Create a mock response and throw it as a "fulfilled" response
      // by using the adapter pattern
      const mockResponse: AxiosResponse = {
        data: responseData,
        status: 200,
        statusText: 'OK (Mock)',
        headers: {},
        config: config as never,
      };

      // Use adapter override to return mock data
      config.adapter = () => Promise.resolve(mockResponse);
    } else {
      log(url, 'WARN', `No mock handler found for ${method.toUpperCase()} ${url}`);
    }

    return config;
  });

  return () => {
    client.interceptors.request.eject(interceptorId);
  };
}

/**
 * Get the validation log for review.
 */
export function getValidationLog() {
  return [...validationLog];
}

/**
 * Print a summary of all mock interactions.
 */
export function printValidationSummary() {
  const total = validationLog.length;
  const ok = validationLog.filter((l) => l.status === 'OK').length;
  const warn = validationLog.filter((l) => l.status === 'WARN').length;
  const error = validationLog.filter((l) => l.status === 'ERROR').length;

  console.log('\n─── Mock API Validation Summary ───');
  console.log(`Total requests: ${total}`);
  console.log(`\x1b[32m  OK:    ${ok}\x1b[0m`);
  console.log(`\x1b[33m  WARN:  ${warn}\x1b[0m`);
  console.log(`\x1b[31m  ERROR: ${error}\x1b[0m`);
  console.log('───────────────────────────────────\n');
}

// Expose on window for console access
if (typeof window !== 'undefined') {
  (window as never as Record<string, unknown>).__mockValidation = {
    getLog: getValidationLog,
    printSummary: printValidationSummary,
  };
}

